import React from 'react';
import { History } from 'lucide-react';
import { Section, fmtDateTime } from './shared';

/**
 * "Request & decision history".
 *
 * Derived from the candidate's own record — when each form went out, when it
 * came back, what was decided since — NOT from the audit log, which is
 * admin-only and would be unreadable here anyway.
 *
 * Shown on both candidate steps and identical on each: a manager asking "when
 * did we last chase this person" should get the same answer wherever they are.
 */
const HistorySection = ({ candidate }) => {
  const pre = candidate.pre_interview || {};
  const checks = candidate.checks || [];
  const extras = candidate.extra_documents || [];
  const references = candidate.references || [];

  const events = [
    pre.sent_at && { at: pre.sent_at, text: 'Pre-interview form sent to the candidate', tone: 'info' },
    pre.submitted_at && { at: pre.submitted_at, text: 'Candidate returned the pre-interview form', tone: 'good' },
    candidate.link_sent_at && { at: candidate.link_sent_at, text: 'Personal details form sent to the candidate', tone: 'info' },
    candidate.submitted_at && { at: candidate.submitted_at, text: 'Candidate returned the personal details form', tone: 'good' },
    candidate.interview_date && {
      at: candidate.interview_date,
      text: `Interview ${candidate.interview_decision === 'Pending' ? 'booked' : `recorded as ${candidate.interview_decision}`}`,
      tone: candidate.interview_decision === 'Successful' ? 'good'
        : candidate.interview_decision === 'Pending' ? 'info' : 'bad',
    },
    ...checks
      .filter((check) => check.decided)
      .map((check) => ({
        at: check.date,
        text: `${check.label} recorded as "${check.status}"${check.reviewed_by ? ` by ${check.reviewed_by}` : ''}`,
        tone: check.status.startsWith('Not ') ? 'bad' : 'good',
      })),
    ...references.flatMap((row) => [
      row.requested_at && {
        at: row.requested_at,
        text: `Reference requested from ${row.name || 'a referee'}${row.requested_by ? ` by ${row.requested_by}` : ''}`,
        tone: 'info',
      },
      row.received_at && {
        at: row.received_at,
        text: `Reference received from ${row.name || 'a referee'}`,
        tone: 'good',
      },
    ]),
    ...extras.flatMap((row) => [
      row.requested_at && {
        at: row.requested_at,
        text: `"${row.label}" requested${row.requested_by ? ` by ${row.requested_by}` : ''}`,
        tone: 'info',
      },
      row.uploaded_at && { at: row.uploaded_at, text: `"${row.label}" received`, tone: 'good' },
    ]),
  ]
    .filter(Boolean)
    // Newest first: the manager is looking for what just happened.
    .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));

  return (
    <Section
      icon={History}
      title="Request & decision history"
      subtitle="Built from this candidate's record — when each form went out, when it came back, and what was decided since."
    >
      {events.length === 0 ? (
        <p className="text-[13px] text-[#94A3B8] dark:text-gray-500">
          Nothing has happened yet — send the pre-interview form to start.
        </p>
      ) : (
        <ol className="space-y-3">
          {events.map((event, index) => (
            <li key={`${event.text}-${index}`} className="flex items-start gap-3">
              <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                event.tone === 'good' ? 'bg-emerald-500'
                  : event.tone === 'bad' ? 'bg-rose-500'
                    : event.tone === 'info' ? 'bg-blue-500' : 'bg-slate-400'
              }`}
              />
              <div className="min-w-0">
                <div className="text-[13px] text-[#0B1220] dark:text-gray-200">{event.text}</div>
                {/* With the time, where the record holds one: two events on the
                    same day otherwise read as simultaneous, and "we sent it and
                    they returned it" is exactly the pair a manager is trying to
                    tell apart. */}
                <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">{fmtDateTime(event.at) || 'Date not recorded'}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
};

export default HistorySection;
